import React, { useState } from 'react';

import {
  FOLLOWUP_CHIP_PALETTE,
  INTENT_LABELS,
} from '../../constants/visitActionPending';
import { dismissFollowup } from '../../services/api/visitActionPendingApi';

/**
 * FollowupBanner — appears above visit content when
 * `doctor_followup_pending_at` is set on the visit and the current
 * viewer is the visit's doctor (or a co-worker — Phase 10).
 *
 * Buttons:
 *   - "Resume" (the parent decides what resume means; we just notify)
 *   - "Dismiss" — explicit clear of the follow-up flag
 *
 * Auto-clear also fires server-side on any visit status transition
 * or sitting completion, so this banner is the soft path; staff who
 * just naturally start consulting (status → IN_PROGRESS) drop the
 * banner without clicking either button.
 */
export default function FollowupBanner({ visit, onResume }) {
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  if (!visit?.doctor_followup_pending_at) return null;

  const reason = INTENT_LABELS[visit.doctor_followup_reason]
    || visit.doctor_followup_reason
    || 'Resume';

  const onDismiss = async () => {
    setBusy(true);
    setErrorMsg(null);
    try {
      await dismissFollowup(visit.visit_id);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const palette = FOLLOWUP_CHIP_PALETTE;

  return (
    <div
      className={[
        'flex items-center justify-between gap-3 rounded-md border px-3 py-2 mb-3',
        palette.bg, palette.border, palette.text,
      ].join(' ')}
    >
      <div className="flex items-center gap-2 text-sm">
        <span aria-hidden="true">↩</span>
        <span className="font-medium">{reason}</span>
        {visit.action_pending_with_member_id ? null : (
          <span className="text-xs opacity-80">— ready to continue</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {onResume && (
          <button
            type="button"
            onClick={onResume}
            disabled={busy}
            className="px-3 py-1 text-xs font-medium rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
          >
            Resume
          </button>
        )}
        <button
          type="button"
          onClick={onDismiss}
          disabled={busy}
          className="px-3 py-1 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-60"
        >
          {busy ? 'Dismissing…' : 'Dismiss'}
        </button>
      </div>
      {errorMsg && (
        <span className="text-xs text-rose-700 dark:text-rose-300">
          {errorMsg}
        </span>
      )}
    </div>
  );
}
